import { styled } from 'styled-components'
import { useResizeObserver } from 'usehooks-ts'
import { useEffect, useMemo, useRef, useState } from 'react'
import { line, scaleLinear } from 'd3'
import { useMFContext } from 'components/MFHelpers'

const scaleWidth = 26

type DrawData = {
  year: number
  value: number
}
const lineProps = ['Schweiz', 'Kanton', 'Gemeinde']
const ttRectWidth = 50
export const LineChart = ({ data }: { data: DrawData[][] }) => {
  const { colorMode } = useMFContext()
  // const [data, setData] = useState<DrawData[][]>([])
  const [dataMax, setDataMax] = useState(0)
  const [tooltipData, setTooltipData] = useState<{
    year: number
    pos: number
    data: { value: number; row: number; y: number }[]
  } | null>()
  const ref = useRef<HTMLDivElement>(null)
  const { width = 0, height = 0 } = useResizeObserver({
    ref,
    box: 'border-box',
  })

  useEffect(() => {
    var valueMax = 0
    data.forEach((d) => {
      const max = Math.max(...d.map((d) => d.value * 100))
      if (max > valueMax) valueMax = max
    })
    setDataMax(ceilToMultiple(valueMax))
  }, [data])
  const plotArea = {
    left: 0,
    top: 40,
    right: width - scaleWidth,
    bottom: height - 20,
  }

  const xScale = useMemo(
    () =>
      scaleLinear().domain([2010, 2024]).range([plotArea.left, plotArea.right]),
    [plotArea.left, plotArea.right]
  )
  const yScale = useMemo(
    () =>
      scaleLinear().domain([0, dataMax]).range([plotArea.bottom, plotArea.top]),
    [dataMax, plotArea.bottom, plotArea.top]
  )
  const l = useMemo(
    () =>
      line<any>()
        .x((d) => xScale(d.year))
        .y((d) => yScale(d.value * 100)),
    [xScale, yScale]
  )
  function LinePath(prop: string) {}

  const colors =
    colorMode === 'light'
      ? ['var(--gray-4)', 'var(--gray-2)', '#ffcf31']
      : ['var(--gray-3)', 'var(--gray-5)', '#ffcf31']
  const textColors =
    colorMode === 'light' ? ['#000', '#fff', '#000'] : ['#fff', '#000', '#000']
  const strokeWidths = [1.5, 1.5, 3]
  const xTicks = [2010, 2012, 2014, 2016, 2018, 2020, 2022, 2024]
  const steps= Math.max(2,Math.round(dataMax/5))
  const yTicks = generateTicks(0, dataMax, steps)
  return (
    <LineChartContainer ref={ref}>
      <SVG
        width={width}
        height={height}
        onPointerLeave={() => setTooltipData(null)}
        onPointerMove={(e) => {
          // get cursor position releative to parent
          const rect = e.currentTarget.getBoundingClientRect()
          const x = e.clientX - rect.left

          const year = Math.round(xScale.invert(x))

          if (data) {
            const values = []
            Object.values(data).forEach((d, i) => {
              d.map((a) => {
                if (+a.year === year) {
                  values.push({
                    value: +a.value,
                    row: i,
                    y: yScale(a.value * 100),
                  })
                }
              })
            })

            setTooltipData({
              year,
              pos: xScale(year),
              data: forceMinYDistance(values),
            })
          }
        }}
      >
        {data.map((d, i) => (
          <>
            <Path
              key={i}
              d={l(d)}
              stroke={colors[i]}
              strokeWidth={strokeWidths[i] + 'px'}
            />
            // Line-End-Circle
            <circle
              cx={xScale(d[d.length - 1].year)}
              cy={yScale(d[d.length - 1].value * 100)}
              r={4}
              fill={colors[i]}
            />
          </>
        ))}
        {xTicks.map((d, i) => (
          <Tick
            key={i}
            x={xScale(d)}
            y={plotArea.bottom}
            textAnchor={
              i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'
            }
            dy='1.5em'
          >
            {d}
          </Tick>
        ))}
        {yTicks.map((m) => {
          const y = yScale(m)
          return (
            <>
              <line
                key={m}
                x1={plotArea.left}
                y1={y}
                x2={plotArea.right + (m === 0 ? scaleWidth : 0)}
                y2={y}
                stroke={
                  m === 0
                    ? 'var(--text-color)'
                    : colorMode === 'light'
                    ? 'var(--gray-4)'
                    : 'var(--gray-3)'
                }
                strokeWidth={m === 0 ? 1 : 0.5}
              />
              {m !== 0 && (
                <Tick
                  x={plotArea.right + 5}
                  y={y}
                  dy='.5em'
                  fontSize={12}
                  fontWeight={500}
                  fontFamily='var(--font-sans-serif)'
                >
                  {m}%
                </Tick>
              )}
            </>
          )
        })}
        {tooltipData && tooltipData.data.length > 0 && (
          <g transform={`translate(${tooltipData.pos},${0})`}>
            <line
              y1={plotArea.top - 20}
              y2={plotArea.bottom}
              stroke='var(--gray-4)'
              strokeWidth={1}
            />
            {tooltipData.data.map((d, i) => {
              const ypos = d.y
              return (
                <>
                  {' '}
                  <rect
                    x={10}
                    y={ypos - 11}
                    width={ttRectWidth}
                    height={22}
                    fill={colors[d.row]}
                    stroke={'var(--site-background)'}
                    strokeWidth={2}
                  />
                  <circle
                    key={i}
                    cx={0}
                    cy={yScale(d.value * 100)}
                    r={6}
                    fill={colors[d.row]}
                  />
                  <text
                    x={ttRectWidth + 5}
                    y={ypos + 5.5}
                    fontSize={12}
                    fontWeight={500}
                    fontFamily='var(--font-sans-serif)'
                    textAnchor='end'
                    fill={textColors[d.row]}
                  >
                    {(d.value * 100).toFixed(2)}%
                  </text>
                </>
              )
            })}
            <text
              x={tooltipData.pos > width / 2 ? -5 : 5}
              y={plotArea.top - 16}
              dy='.5em'
              fontSize={12}
              fontWeight={500}
              fontFamily='var(--font-sans-serif)'
              textAnchor={tooltipData.pos > width / 2 ? 'end' : 'start'}
            >
              {tooltipData.year}
            </text>
          </g>
        )}
      </SVG>
    </LineChartContainer>
  )
}

const Tick = styled.text`
  font-size: 12px;
  font-family: var(--font-sans-serif);
  font-weight: 500;
  fill: var(--text-color);
`

const LineChartContainer = styled.div`
  width: 100%;
  height: 300px;
`

const Path = styled.path`
  fill: none;
`

const SVG = styled.svg`
  height: 100%;
  overflow: visible;
`

function ceilToMultiple(val: number, multiple: number = 1) {
  return Math.ceil(val / multiple) * multiple
}
function generateTicks(min: number, max: number, multiple: number = 1) {
  const ticks = []
  for (let i = min; i <= max; i += multiple) {
    ticks.push(i)
  }
  return ticks
}

function forceMinYDistance(data: { value: number; y: number; row: number }[]) {
  const minDistance = 24
  const sortedData = data.sort((a, b) => a.y - b.y)
  var currentAdd = 0
  sortedData.forEach((d, i) => {
    if (i === 0) return d
    if (d.y - sortedData[i - 1].y < minDistance) {
      currentAdd += minDistance - (d.y - sortedData[i - 1].y)
      d.y += minDistance - (d.y - sortedData[i - 1].y)
    }
  })
  return sortedData
}
